import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";
import coverAlternative from "@/assets/blog/alternative-email-marketing/couverture.webp";
import coverFidelisation from "@/assets/blog/outils-fidelisation-client/couverture.webp";
import coverProgrammes from "@/assets/blog/programmes-fidelite/couverture.webp";
import coverNotifications from "@/assets/blog/notifications-push-wallet/couverture.webp";
import coverKpi from "@/assets/blog/kpi-fidelite-client/couverture.webp";
import coverPetitCommerce from "@/assets/blog/outil-fidelisation-petit-commerce/couverture.webp";
import coverFidelisationGuide from "@/assets/blog/fidelisation-client-guide/couverture.webp";
import coverPourquoiPartent from "@/assets/blog/pourquoi-clients-partent/couverture.webp";
import coverEmotionnelle from "@/assets/blog/fidelisation-emotionnelle-vs-transactionnelle/couverture.webp";
import coverAmbassadeur from "@/assets/blog/client-ambassadeur/couverture.webp";
import coverActions from "@/assets/blog/actions-fidelisation-client/couverture.webp";
import coverPushMarketing from "@/assets/blog/push-marketing/couverture.webp";
import coverPushVsPull from "@/assets/blog/push-vs-pull-marketing/couverture.webp";
import coverNotifPushTaux from "@/assets/blog/notifications-push-taux-ouverture/couverture.webp";
import coverPushWalletSansApp from "@/assets/blog/notifications-push-wallet-sans-app/couverture.webp";

const articles = [
  {
    slug: "/notifications-push-wallet",
    cover: coverPushWalletSansApp,
    alt: "Fonctionnement du push wallet avec notifications envoyées sans app ni filtre sur écran verrouillé du téléphone",
    title: "Push wallet : envoyer des notifications push sans app ni algorithme en 2026",
    excerpt: "Un canal direct qui contourne filtres et intermédiaires pour atteindre vos clients sur leur écran au bon moment. Fonctionnement, avantages et mise en place.",
    date: "17 mai 2026",
    readTime: "10 min",
  },
  {
    slug: "/notifications-push-taux-ouverture",
    cover: coverNotifPushTaux,
    alt: "Fonctionnement des notifications push avec affichage sur smartphone et comparaison des formats app navigateur et wallet",
    title: "Notifications push : comment ça fonctionne et pourquoi le taux d'ouverture dépasse 50 %",
    excerpt: "Pourquoi certains messages sont vus immédiatement ? Comprenez les formats, leurs différences et comment capter l’attention au bon moment.",
    date: "10 mai 2026",
    readTime: "10 min",
  },
  {
    slug: "/push-vs-pull-marketing",
    cover: coverPushVsPull,
    alt: "Comparaison entre push et pull marketing avec stratégies pour attirer ou contacter les clients selon le contexte",
    title: "Push vs pull marketing : différences, avantages et comment choisir en 2026",
    excerpt: "Découvrez comment articuler attraction et prise de contact selon vos objectifs, avec une grille simple pour choisir le bon canal à chaque étape.",
    date: "03 mai 2026",
    readTime: "10 min",
  },
  {
    slug: "/push-marketing",
    cover: coverPushMarketing,
    alt: "Stratégie de push marketing avec canaux comme email SMS notifications et wallet pour contacter les clients directement",
    title: "Push marketing : définition, canaux et stratégie complète 2026",
    excerpt: "Comprenez quand initier le contact, quels canaux utiliser et comment structurer vos campagnes pour toucher vos clients au bon moment.",
    date: "28 avril 2026",
    readTime: "11 min",
  },
  {
    slug: "/7-actions-fidelisation-client",
    cover: coverActions,
    alt: "Actions concrètes de fidélisation client faciles à appliquer rapidement pour augmenter la rétention et l’engagement",
    title: "Fidélisation client : 7 actions concrètes à mettre en place dès cette semaine",
    excerpt: "7 actions de fidélisation réalisables en une semaine, sans budget ni expertise technique. Une roadmap claire sur 30 jours pour démarrer dès aujourd'hui.",
    date: "22 avril 2026",
    readTime: "9 min",
  },
  {
    slug: "/client-ambassadeur",
    cover: coverAmbassadeur,
    alt: "Comment transformer un client satisfait en client ambassadeur avec stratégie de recommandation et fidélisation",
    title: "Comment transformer un client satisfait en client ambassadeur",
    excerpt: "La majorité des clients satisfaits ne recommandent jamais spontanément. Découvrez les conditions et mécanismes concrets pour transformer vos clients en ambassadeurs actifs.",
    date: "16 avril 2026",
    readTime: "10 min",
  },
  {
    slug: "/fidelisation-emotionnelle-vs-transactionnelle",
    cover: coverEmotionnelle,
    alt: "Comparaison entre fidélisation émotionnelle et fidélisation transactionnelle avec impact sur la loyauté client",
    title: "Fidélisation émotionnelle vs transactionnelle : laquelle crée une vraie loyauté ?",
    excerpt: "Un programme de fidélité avec des points ne suffit pas à retenir vos clients. Découvrez pourquoi la fidélisation émotionnelle crée une loyauté durable et comment combiner les deux approches.",
    date: "11 avril 2026",
    readTime: "12 min",
  },
  {
    slug: "/pourquoi-clients-partent",
    cover: coverPourquoiPartent,
    alt: "Illustration des clients qui partent sans se plaindre avec analyse du sentiment de négligence et fidélisation client",
    title: "Pourquoi 68 % des clients partent sans jamais se plaindre (et comment les retenir)",
    excerpt: "68 % des clients qui quittent une entreprise le font parce qu'ils se sentaient négligés. Découvrez comment détecter les signaux et les retenir à temps.",
    date: "6 avril 2026",
    readTime: "10 min",
  },
  {
    slug: "/fidelisation-client-guide",
    cover: coverFidelisationGuide,
    alt: "Illustration de la fidélisation client avec stratégies et outils pour améliorer la rétention et la relation client en 2026",
    title: "Comment fidéliser ses clients : le guide complet 2026",
    excerpt: "Fidéliser ses clients n'est pas une option réservée aux grandes enseignes. Découvrez les stratégies, outils et actions concrètes pour améliorer la rétention et développer votre activité.",
    date: "31 mars 2026",
    readTime: "12 min",
  },
  {
    slug: "/outil-fidelisation-petit-commerce",
    cover: coverPetitCommerce,
    alt: "Illustration des outils de fidélisation pour petit commerce avec solution simple et accessible sans gros budget",
    title: "Outil de fidélisation pour petit commerce : par où commencer sans gros budget",
    excerpt: "Les grandes enseignes ont des équipes marketing et des budgets à six chiffres. Vous, non. Découvrez quel outil de fidélisation utiliser sans gros budget, en 3 étapes simples.",
    date: "26 mars 2026",
    readTime: "10 min",
  },
  {
    slug: "/mesurer-fidelite-client-kpi",
    cover: coverKpi,
    alt: "Illustration des KPI de fidélité client avec indicateurs et analyse de performance pour mesurer la rétention",
    title: "Comment mesurer la fidélité client : les 6 KPI indispensables",
    excerpt: "Mesurer la fidélité client efficacement : découvrez les 6 KPI indispensables, leurs calculs et comment savoir si vos actions fonctionnent vraiment.",
    date: "25 mars 2026",
    readTime: "14 min",
  },
  {
    slug: "/notifications-push-wallet-fidelisation",
    cover: coverNotifications,
    alt: "Illustration des notifications push wallet sur smartphone avec écran verrouillé et message marketing visible en 2026",
    title: "Notifications push wallet : le canal de fidélisation qui cartonne en 2026",
    excerpt: "Les notifications push wallet révolutionnent la fidélisation client en 2026 : découvrez un canal direct avec fort taux d'ouverture, sans email ni SMS.",
    date: "23 mars 2026",
    readTime: "12 min",
  },
  {
    slug: "/programme-fidelite-ne-fonctionne-pas",
    cover: coverProgrammes,
    alt: "Programme de fidélité ne fonctionne pas",
    title: "Pourquoi 58 % des programmes de fidélité sont abandonnés",
    excerpt: "58 % des membres d'un programme de fidélité ne l'utilisent plus après 6 mois. Découvrez les 5 erreurs qui tuent un programme et les correctifs à appliquer.",
    date: "18 mars 2026",
    readTime: "10 min",
  },
  {
    slug: "/meilleurs-outils-de-fidelisation-client",
    cover: coverFidelisation,
    alt: "Les meilleurs outils de fidélisation client",
    title: "Les meilleurs outils de fidélisation client : le guide complet 2026",
    excerpt: "Acquérir un nouveau client coûte 5 à 7 fois plus cher que d'en fidéliser un existant. Découvrez les 7 outils de fidélisation client les plus efficaces en 2026.",
    date: "13 mars 2026",
    readTime: "15 min",
  },
  {
    slug: "/alternative-email-marketing",
    cover: coverAlternative,
    alt: "Alternative à l'email marketing : quelles solutions en 2026 ?",
    title: "Alternative à l'email marketing : quelles solutions en 2026 ?",
    excerpt: "Pendant des années, l'email marketing a été le canal roi. Mais en 2026, une réalité s'impose : l'email marketing ne produit plus les mêmes résultats.",
    date: "4 février 2026",
    readTime: "12 min",
  },
];

const Blog = () => {
  const jsonLdSchemas = [
    generateWebPageSchema("Blog", seoConfig.blog.description, "/blog"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" }
    ])
  ];

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blog} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-10 sm:pb-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155] mb-6">
              Ressources
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Blog <span className="text-[#0c97a9]">Unotif</span>
            </h1>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-2xl mx-auto">
              Consultez nos conseils, stratégies et informations concernant le marketing Wallet et les notifications push
            </p>
          </div>
        </section>

        {/* Featured Article */}
        <section className="pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to={featured.slug} className="group block">
              <article className="grid grid-cols-1 lg:grid-cols-5 bg-white rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-lg transition-shadow duration-300">
                <div className="overflow-hidden lg:col-span-3">
                  <img
                    src={featured.cover}
                    alt={featured.alt}
                    className="w-full h-full object-cover object-left aspect-[16/10] lg:aspect-auto lg:min-h-[340px] group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-8 lg:col-span-2">
                  <div className="flex items-center gap-3 text-xs font-semibold text-[#0c97a9] uppercase tracking-wide mb-3">
                    <span>À la une</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-3 group-hover:text-[#0c97a9] transition-colors leading-snug">
                    {featured.title}
                  </h2>
                  <p className="text-[14px] sm:text-[15px] font-medium leading-relaxed text-[#495456] mb-5 line-clamp-3">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm font-medium text-[#495456]">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {featured.date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {featured.readTime}
                      </span>
                    </div>
                    <span className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#0c97a9] group-hover:gap-2 transition-all">
                      Lire <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </article>
            </Link>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {rest.map((article) => (
                <Link key={article.slug} to={article.slug} className="group block">
                  <article className="bg-white rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                    <div className="overflow-hidden">
                      <img
                        src={article.cover}
                        alt={article.alt}
                        className="w-full aspect-[16/10] object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-xs font-medium text-[#495456] mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {article.readTime}
                        </span>
                      </div>
                      <h2 className="text-[15px] sm:text-base font-bold text-[#022226] mb-2 group-hover:text-[#0c97a9] transition-colors leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-[13px] sm:text-sm font-medium leading-relaxed text-[#495456] line-clamp-3 flex-1">
                        {article.excerpt}
                      </p>
                      <span className="mt-4 flex items-center gap-1 text-sm font-semibold text-[#0c97a9] group-hover:gap-2 transition-all">
                        Lire l'article <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
